// <track-order> — order lookup + result view for /pages/track-order.
// Calls the spotter-track service. Order number and email are sent in a POST
// body only, never in the URL. `?demo=1` shows a built-in sample (no network).

const STEP_KEYS = ['placed', 'packed', 'in_transit', 'arrived_us', 'out_for_delivery', 'delivered'];

// Matches Figma 170:3841 (order #1042, Tape + Ice, Austin TX).
const DEMO_ORDER = {
  name: '#1042',
  createdAt: '2026-09-20T15:00:00Z',
  cancelled: false,
  stage: 'in_transit',
  partiallyShipped: false,
  items: [
    { title: 'Tape', variantTitle: '60 pimple patches', quantity: 1, total: '$22' },
    { title: 'Ice', variantTitle: '5 pairs under-eye patches', quantity: 1, total: '$45' },
  ],
  shipping: 'Free',
  shipTo: { name: 'Alex R.', city: 'Austin', provinceCode: 'TX', zip: '78701' },
  fulfillments: [
    {
      createdAt: '2026-09-22T15:00:00Z',
      displayStatus: 'IN_TRANSIT',
      trackingCompany: 'USPS',
      trackingNumber: '9400100000000000000000',
      trackingUrl: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=9400100000000000000000',
      estimatedDeliveryAt: null,
      lastEvent: { status: 'IN_TRANSIT', happenedAt: '2026-09-24T15:00:00Z', message: 'Departed origin facility' },
    },
  ],
  timeline: [
    { key: 'placed', state: 'done', date: '2026-09-20T15:00:00Z' },
    { key: 'packed', state: 'done', date: '2026-09-22T15:00:00Z' },
    { key: 'in_transit', state: 'current', date: '2026-09-24T15:00:00Z' },
    { key: 'arrived_us', state: 'upcoming', date: null },
    { key: 'out_for_delivery', state: 'upcoming', date: null },
    { key: 'delivered', state: 'upcoming', date: null },
  ],
  estimatedDelivery: { type: 'range', from: '2026-10-01', to: '2026-10-09' },
};

const fill = (tpl, vars) => tpl.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? ''));

// Dates: "YYYY-MM-DD" are calendar days (UTC); full timestamps use local time.
function toDate(value) {
  if (!value) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value + 'T12:00:00Z') : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
function fmt(value, opts) {
  const d = toDate(value);
  if (!d) return '';
  const dayOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return d.toLocaleDateString('en-US', { ...opts, ...(dayOnly ? { timeZone: 'UTC' } : {}) });
}
const monthDay = (v) => fmt(v, { month: 'short', day: 'numeric' });
const dayOnly = (v) => fmt(v, { day: 'numeric' });
const sameMonth = (a, b) => fmt(a, { month: 'short', year: 'numeric' }) === fmt(b, { month: 'short', year: 'numeric' });

function groupTracking(num) {
  if (!num) return '';
  return /^\d{12,}$/.test(num) ? num.replace(/(\d{4})(?=\d)/g, '$1 ') : num;
}

class TrackOrder extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    try {
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
    } catch {
      this.copy = { headlines: {}, steps: {}, ui: {} };
    }
    // Translation strings arrive HTML-escaped (e.g. &#39;); decode to plain text.
    const decoder = document.createElement('textarea');
    for (const [k, v] of Object.entries(this.copy.ui || {})) {
      decoder.innerHTML = v;
      this.copy.ui[k] = decoder.value;
    }
    this.apiUrl = this.dataset.apiUrl;
    this.form = this.querySelector('[data-form]');
    this.orderInput = this.querySelector('[data-order]');
    this.emailInput = this.querySelector('[data-email]');
    this.errorEl = this.querySelector('[data-error]');
    this.statusEl = this.querySelector('[data-status]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.spinner = this.querySelector('[data-spinner]');

    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.querySelector('[data-back]').addEventListener('click', () => this.showLookup());
    [this.orderInput, this.emailInput].forEach((el) =>
      el.addEventListener('input', () => el.removeAttribute('aria-invalid')),
    );

    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === '1') this.render(DEMO_ORDER, { demo: true, focus: false });
  }

  setState(state) {
    this.dataset.state = state;
    this.querySelectorAll('[data-show]').forEach((el) => {
      el.hidden = el.dataset.show !== state;
    });
  }

  setBusy(busy) {
    this.submitBtn.disabled = busy;
    this.submitBtn.setAttribute('aria-busy', String(busy));
    this.spinner.hidden = !busy;
    this.statusEl.textContent = busy ? this.copy.ui.loading || '' : '';
  }

  showError(msg, invalidEls = []) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = !msg;
    [this.orderInput, this.emailInput].forEach((el) => {
      if (invalidEls.includes(el)) {
        el.setAttribute('aria-invalid', 'true');
        el.setAttribute('aria-errormessage', this.errorEl.id || '');
      } else el.removeAttribute('aria-invalid');
    });
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.submitBtn.disabled) return;
    const order = this.orderInput.value.trim();
    const email = this.emailInput.value.trim();
    const bad = [];
    if (!/^#?\s*\d{1,10}$/.test(order)) bad.push(this.orderInput);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) bad.push(this.emailInput);
    if (bad.length) {
      this.showError(this.copy.ui.errInvalid, bad);
      bad[0].focus();
      return;
    }
    this.showError('');
    this.setBusy(true);
    try {
      const res = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order, email }),
        credentials: 'omit',
        cache: 'no-store',
        referrerPolicy: 'strict-origin',
      });
      if (res.ok) {
        const data = await res.json();
        this.setBusy(false);
        this.render(data.order, { demo: false, focus: true });
        return;
      }
      this.setBusy(false);
      if (res.status === 404) this.showError(this.copy.ui.errNotFound);
      else if (res.status === 429) this.showError(this.copy.ui.errRate);
      else if (res.status === 400) this.showError(this.copy.ui.errInvalid, [this.orderInput, this.emailInput]);
      else this.showError(this.copy.ui.errNetwork);
    } catch {
      this.setBusy(false);
      this.showError(this.copy.ui.errNetwork);
    }
  }

  showLookup() {
    this.setState('lookup');
    this.orderInput.focus();
  }

  render(order, { demo, focus }) {
    const { ui, headlines } = this.copy;
    const q = (sel) => this.querySelector(sel);

    q('[data-order-name]').textContent = fill(ui.orderPrefix || 'Order {{ name }}', { name: order.name });
    q('[data-sample]').hidden = !demo;
    q('[data-headline]').textContent = headlines[order.cancelled ? 'cancelled' : order.stage] || '';
    q('[data-partial]').hidden = !order.partiallyShipped || order.cancelled;

    // Estimated delivery line
    const eta = order.estimatedDelivery;
    let etaD = '';
    let etaM = '';
    if (order.cancelled) {
      etaD = etaM = ui.cancelledNote || '';
    } else if (eta?.type === 'delivered') {
      etaD = etaM = fill(ui.deliveredOn, { date: monthDay(eta.date) });
    } else if (eta) {
      const single = !eta.to || monthDay(eta.from) === monthDay(eta.to);
      const range = single ? monthDay(eta.from) : `${monthDay(eta.from)} – ${monthDay(eta.to)}`;
      const short = single ? monthDay(eta.from) : `${monthDay(eta.from)} – ${sameMonth(eta.from, eta.to) ? dayOnly(eta.to) : monthDay(eta.to)}`;
      etaD = fill(ui.etaDesktop, { range });
      etaM = fill(ui.etaMobile, { range: short });
    }
    q('[data-eta-desktop]').textContent = etaD;
    q('[data-eta-mobile]').textContent = etaM;
    q('[data-eta]').hidden = !etaD;

    this.renderTimeline(order);
    this.renderTracking(order);
    this.renderItems(order);

    const st = order.shipTo;
    const shipCard = q('[data-shipto-card]');
    const shipEl = q('[data-shipto]');
    shipEl.replaceChildren();
    if (st) {
      const line2 = [st.city, [st.provinceCode, st.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      [st.name, line2].filter(Boolean).forEach((line, i) => {
        if (i) shipEl.append(document.createElement('br'));
        shipEl.append(document.createTextNode(line));
      });
    }
    shipCard.hidden = !st;

    this.setState('result');
    this.statusEl.textContent = demo ? '' : ui.found || '';
    if (focus) {
      const h = q('[data-headline]');
      h.focus({ preventScroll: true });
      h.closest('.track-order__result').scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  renderTimeline(order) {
    const list = this.querySelector('[data-timeline]');
    const tpl = this.querySelector('[data-tpl-step]');
    const { steps, ui } = this.copy;
    list.replaceChildren();
    list.hidden = order.cancelled;
    if (order.cancelled) return;
    const byKey = Object.fromEntries((order.timeline || []).map((s) => [s.key, s]));
    STEP_KEYS.forEach((key) => {
      const step = byKey[key] || { state: 'upcoming' };
      const li = tpl.content.firstElementChild.cloneNode(true);
      li.classList.add(`track-order__step--${step.state}`);
      if (step.state === 'current') li.setAttribute('aria-current', 'step');
      const [title, desc] = steps[key] || ['', ''];
      li.querySelector('[data-title]').textContent = title;
      li.querySelector('[data-desc]').textContent = desc;
      const dateEl = li.querySelector('[data-date]');
      if (step.state === 'current') dateEl.textContent = ui.now || 'Now';
      else if (step.state === 'done' && step.date) dateEl.textContent = monthDay(step.date);
      else dateEl.remove();
      li.querySelector('[data-sr-state]').textContent = `(${ui[step.state] || step.state})`;
      list.append(li);
    });
  }

  renderTracking(order) {
    const wrap = this.querySelector('[data-tracking]');
    const tpl = this.querySelector('[data-tpl-tracking]');
    const { ui } = this.copy;
    wrap.replaceChildren();
    const shipments = (order.fulfillments || []).filter((f) => f.trackingNumber || f.trackingCompany);
    if (!shipments.length) {
      if (order.cancelled) return;
      const card = tpl.content.firstElementChild.cloneNode(true);
      card.querySelector('[data-company]').textContent = ui.notShipped || '';
      card.querySelector('[data-number]').remove();
      card.querySelector('[data-last]').textContent = ui.notShippedNote || '';
      wrap.append(card);
      return;
    }
    shipments.forEach((f) => {
      const card = tpl.content.firstElementChild.cloneNode(true);
      card.querySelector('[data-company]').textContent = f.trackingCompany || '';
      card.querySelector('[data-number]').textContent = groupTracking(f.trackingNumber);
      const last = f.lastEvent;
      card.querySelector('[data-last]').textContent = last
        ? fill(ui.lastUpdate, { date: monthDay(last.happenedAt), message: (last.message || '').replace(/\.?$/, '.') })
        : ui.noScans || '';
      const link = card.querySelector('[data-link]');
      if (f.trackingUrl && /^https:\/\//i.test(f.trackingUrl)) {
        link.href = f.trackingUrl;
        link.hidden = false;
      }
      wrap.append(card);
    });
  }

  renderItems(order) {
    const list = this.querySelector('[data-items]');
    const tpl = this.querySelector('[data-tpl-item]');
    list.replaceChildren();
    const row = (name, price) => {
      const li = tpl.content.firstElementChild.cloneNode(true);
      li.querySelector('[data-name]').textContent = name;
      li.querySelector('[data-price]').textContent = price;
      list.append(li);
    };
    (order.items || []).forEach((it) => {
      let name = [it.title, it.variantTitle].filter(Boolean).join(' · ');
      if (it.quantity > 1) name += ` × ${it.quantity}`;
      row(name, it.total || '');
    });
    if (order.shipping) row(this.copy.ui.shipping || 'Shipping', order.shipping);
  }
}

if (!customElements.get('track-order')) customElements.define('track-order', TrackOrder);
